import axios, { AxiosInstance } from 'axios';
import baseAPI from '@/api/base';
import store from '@/store';
import { KODE_AI_USAGE_URL } from '@/utils/urls';
import type { KodeAiUsageSnapshot } from '@/utils/kode_ai_usage';

/**
 * Kode AI's use of Gemini: the API's reading, for the Kode AI page
 * (components/kode_ai/Kode_AI_Usage) and the dashboard's card.
 *
 * Its own client rather than api/base: both ask every few seconds, and a
 * failed ask is shown where it was made, not as the app's error toast each
 * time.
 *
 * Made on first use, not when this file loads: api/base imports the router,
 * which loads the dashboard and so this file, while api/base itself is still
 * loading — its client doesn't exist yet at that point.
 */
let kodeApi: AxiosInstance | null = null;

const client = (): AxiosInstance => {
  if (kodeApi) return kodeApi;
  kodeApi = axios.create({
    baseURL: baseAPI.defaults.baseURL,
    headers: {
      'Content-type': 'application/json',
    },
    timeout: 20 * 1000,
  });
  kodeApi.interceptors.request.use((request) => {
    const token = store.getters.sessionToken;
    if (token) request.headers.Authorization = `Bearer ${token}`;
    return request;
  });
  return kodeApi;
};

export interface KodeAiResult<T> {
  ok: boolean;
  data: T | null;
  /** What the API said when it refused, to show where the ask was made. */
  error: string | null;
  status: number | null;
}

async function request<T>(call: Promise<any>): Promise<KodeAiResult<T>> {
  try {
    const response = await call;
    if (response && response.data && response.data.status) {
      return { ok: true, data: response.data.data as T, error: null, status: response.status };
    }
    return { ok: false, data: null, error: (response && response.data && response.data.message) || null, status: response ? response.status : null };
  } catch (error: any) {
    const response = error && error.response;
    return { ok: false, data: null, error: (response && response.data && response.data.message) || null, status: response ? response.status : null };
  }
}

export const fetchKodeAiUsage = (): Promise<KodeAiResult<KodeAiUsageSnapshot>> =>
  request<KodeAiUsageSnapshot>(client().get(KODE_AI_USAGE_URL));

export interface KodeAiUsageWatch {
  /** Asks at once (a retry, the day's reset). */
  refresh: () => Promise<void>;
  stop: () => void;
}

/**
 * Keeps a reading coming: one now, then every `everyMs` while the tab is in
 * view, and one at once when it comes back into view. `onResult` gets each
 * answer and when its ask went out (to set the server's clock against this
 * browser's). Never two asks at a time.
 */
export function watchKodeAiUsage(
  everyMs: number,
  onResult: (result: KodeAiResult<KodeAiUsageSnapshot>, sentAt: number) => void,
): KodeAiUsageWatch {
  let stopped = false;
  let inFlight = false;
  let timer: number | undefined;

  const ask = async (): Promise<void> => {
    if (stopped || inFlight) return;
    inFlight = true;
    const sentAt = Date.now();
    const result = await fetchKodeAiUsage();
    inFlight = false;
    if (!stopped) onResult(result, sentAt);
  };

  const schedule = (): void => {
    window.clearTimeout(timer);
    if (stopped) return;
    timer = window.setTimeout(async () => {
      if (document.visibilityState !== 'hidden') await ask();
      schedule();
    }, everyMs);
  };

  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') ask();
  };

  document.addEventListener('visibilitychange', onVisibility);
  ask();
  schedule();

  return {
    refresh: ask,
    stop: () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
